import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Api } from 'src/models/Api.model';
import { EntityCriteriaOperatorEnum } from 'src/models/GalenData/EntityCriteria.model';
import { PageOfUser } from 'src/models/GalenData/PageOfUser.model';
import { UserRoleEnum } from 'src/models/GalenData/UserRole.model';
import { I18NextNS } from 'src/models/Language.model';

import useHandleHttpRequestError from './useHandleHttpRequestError';

export default function useGetUsers() {
  const [users, setUsers] = useState<PageOfUser>({});
  const [isGettingUsers, setIsGettingUsers] = useState(false);

  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.COMMON);

  const { handleHttpRequestError } = useHandleHttpRequestError();

  const handleGetUsers = useCallback(
    async (
      pageSize?: number,
      pageNumber: number = 0,
      searchValues: { emailAddress: string; name: string } = {
        emailAddress: '',
        name: '',
      },
    ) => {
      try {
        setIsGettingUsers(true);

        const results = await Api.remote._get_users_advanced(
          [
            {
              key: 'role',
              operator: EntityCriteriaOperatorEnum.NotEqual,
              value: UserRoleEnum.Patient,
            },
            {
              key: 'emailAddress',
              operator: EntityCriteriaOperatorEnum.Like,
              value: searchValues.emailAddress.trim(),
            },
            {
              key: 'name',
              operator: EntityCriteriaOperatorEnum.Like,
              value: searchValues.name.trim(),
            },
          ],
          {
            pageSize,
            pageNumber,
            sortBy: 'createdOn',
            sortOrder: 'DESC',
          },
        );

        const usersData = {
          content: results.data.content,
          totalElements: results.data.totalElements,
          totalPages: results.data.totalPages,
          size: pageSize,
          number: pageNumber,
        };

        setUsers(usersData);

        return usersData;
      } catch (error) {
        const errorMessage = handleHttpRequestError(error);
        Api.alertBox({
          title: t('bootbox.title.Error'),
          message: `${t('message.error.failedToLoadUsers')} ${errorMessage}`,
          locale: language,
          type: 'error',
        });

        throw error;
      } finally {
        setIsGettingUsers(false);
      }
    },
    [handleHttpRequestError, language, t],
  );

  return { users, handleGetUsers, isGettingUsers };
}
